import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { positionAsset } from 'js/src/agent/positioningUtils';
import { checkpointVideo } from 'js/src/data/video-data';
import { saveRawLuminary } from 'js/src/luminary/index';

export const legacySetAssetGroupSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    asset_name: z.string().describe('The group asset name to modify'),
    position: z
        .object({
            x: z.number().describe('X position'),
            y: z.number().describe('Y position'),
        })
        .optional()
        .describe('The group asset position as an object with x and y coordinates'),
    max_height: z
        .number()
        .min(0)
        .optional()
        .describe('The maximum height of the group asset in pixels'),
    align_children: z
        .enum(['left', 'center', 'right'])
        .default('left')
        .describe('Alignment of text children in the group'),
});
export type SetAssetGroupArgs = z.infer<typeof legacySetAssetGroupSchema>;

export class LegacySetAssetGroupTool extends VideoStateTool<typeof legacySetAssetGroupSchema> {
    public static readonly toolName = 'set_group_asset';

    protected override readonly schema = legacySetAssetGroupSchema;
    protected override readonly toolName = LegacySetAssetGroupTool.toolName;
    protected override readonly toolDescription =
        'Set group asset properties for a specific scene. When the user requests multiple changes (e.g., position AND max_height), include ALL requested properties in a SINGLE call.';

    public override getToolDisplayName(): string {
        return 'Setting asset group';
    }

    public get statusIcon(): string {
        return 'group_work';
    }

    public async execute(args: SetAssetGroupArgs, context: ToolExecutionContext) {
        const { scene_index, asset_name, position, max_height, align_children } = args;

        try {
            // Business logic validation (schema validation already done)
            if (
                position === undefined &&
                max_height === undefined &&
                align_children === undefined
            ) {
                return 'Error: At least one of position or max_height or align_children must be provided';
            }

            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${scene_index}`;
            }

            if (!VideoData.isAssetGroup(asset)) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not a group asset`;
            }

            const assetGroup = asset;
            const { parentActionGroup } = context;

            const changes: string[] = [];

            try {
                // Set position if provided
                if (position !== undefined) {
                    if (typeof position !== 'object' || position === null) {
                        return 'Error: position must be an object with x and y properties';
                    }
                    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
                        return 'Error: position.x and position.y must be numbers';
                    }
                    positionAsset(assetGroup, {
                        x: position.x,
                        y: position.y,
                    });
                    changes.push(`position to (${position.x}, ${position.y})`);
                }

                // Set max_height if provided
                if (max_height !== undefined) {
                    if (typeof max_height !== 'number' || max_height < 0) {
                        return 'Error: max_height must be a non-negative number';
                    }
                    assetGroup.maxHeight = max_height;
                    changes.push(`max_height to ${max_height}px`);
                }

                // Set alignment of text children if provided
                if (align_children !== undefined) {
                    if (!['left', 'center', 'right'].includes(align_children)) {
                        return 'Error: align_children must be one of "left", "center", or "right"';
                    }
                    console.log('setting alignChildren', align_children);
                    assetGroup.alignChildren = align_children;
                    changes.push(`align_children to ${align_children}`);
                }

                // Save changes
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                return `Success: Updated ${changes.join(', ')} for asset group ${asset_name} in scene ${scene_index}`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error updating asset group properties: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting asset group: ${errorMessage}`;
        }
    }
}
